import { createContext, useContext, useCallback, useMemo, type ReactNode } from 'react'
import { doc, arrayUnion, arrayRemove } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { setDoc } from '@/lib/firestore'
import { useAuth } from '@/hooks/useAuth'
import { useFirestoreDoc } from '@/hooks/useFirestore'
import { runMutation } from '@/lib/firestoreMutation'
import { track } from '@/lib/analytics'

export type FavoriteSource = 'friends_list' | 'profile' | 'festival_mode'

interface FavoriteFriendsValue {
  favoriteIds: string[]
  favoriteIdSet: Set<string>
  loading: boolean
  isFavorite: (userId: string) => boolean
  toggleFavorite: (userId: string, source: FavoriteSource) => Promise<void>
}

const EMPTY_SET: Set<string> = new Set()

const FavoriteFriendsCtx = createContext<FavoriteFriendsValue>({
  favoriteIds: [],
  favoriteIdSet: EMPTY_SET,
  loading: true,
  isFavorite: () => false,
  toggleFavorite: async () => {},
})

// Full API — mark/unmark + read. For the Friends list and profile star toggles.
// eslint-disable-next-line react-refresh/only-export-components -- the context's hooks live next to their provider
export function useFavoriteFriends() {
  return useContext(FavoriteFriendsCtx)
}

// Read-only slice — the current user's favorite ids. Used by presentational
// surfaces (facepiles, schedule filter) that only need to know who's favorited.
// eslint-disable-next-line react-refresh/only-export-components -- the context's hooks live next to their provider
export function useFavoriteIdSet() {
  return useContext(FavoriteFriendsCtx).favoriteIdSet
}

export function FavoriteFriendsProvider({ children }: { children: ReactNode }) {
  const { signedInUid: uid } = useAuth()

  // Private, owner-only doc (users/{uid}/private/favorites) — favorites stay
  // visible only to the user who set them.
  const ref = uid ? doc(db, 'users', uid, 'private', 'favorites') : null
  const { data, loading } = useFirestoreDoc<{ favoriteFriendIds?: string[] }>(ref)

  const favoriteIds = useMemo(() => data?.favoriteFriendIds ?? [], [data])
  const favoriteIdSet = useMemo(() => new Set(favoriteIds), [favoriteIds])

  const isFavorite = useCallback((userId: string) => favoriteIdSet.has(userId), [favoriteIdSet])

  const toggleFavorite = useCallback(
    async (userId: string, source: FavoriteSource) => {
      if (!uid) return
      const nowFavorite = !favoriteIdSet.has(userId)
      await runMutation(nowFavorite ? "Couldn't add favorite" : "Couldn't remove favorite", async () => {
        await setDoc(
          doc(db, 'users', uid, 'private', 'favorites'),
          { favoriteFriendIds: nowFavorite ? arrayUnion(userId) : arrayRemove(userId) },
          { merge: true },
        )
        track(nowFavorite ? 'friend_favorited' : 'friend_unfavorited', { entry_point: source })
      })
    },
    [uid, favoriteIdSet],
  )

  const value = useMemo<FavoriteFriendsValue>(
    () => ({ favoriteIds, favoriteIdSet, loading, isFavorite, toggleFavorite }),
    [favoriteIds, favoriteIdSet, loading, isFavorite, toggleFavorite],
  )

  return <FavoriteFriendsCtx.Provider value={value}>{children}</FavoriteFriendsCtx.Provider>
}
